/**
 * useChunkLoadingError Hook
 * 
 * Detects when dynamic imports fail due to stale deployments
 * (chunk files no longer available after a new version is deployed).
 * 
 * Returns a flag indicating if an update is required.
 */

import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

export function useChunkLoadingError() {
  const [updateRequired, setUpdateRequired] = useState(false);

  // Check if an error is a chunk loading error
  const isChunkLoadError = useCallback((error: Error | string): boolean => {
    const message = typeof error === 'string' ? error : error.message;
    return (
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      message.includes('Loading CSS chunk') ||
      message.includes('ChunkLoadError') ||
      // Vite-specific error patterns
      message.includes('Unable to preload CSS') ||
      message.includes('error loading dynamically imported module')
    );
  }, []);

  // Handle the error by showing the update modal
  const handleChunkError = useCallback(() => {
    logger.warn('[ChunkLoadingError] Stale deployment detected, showing update modal');
    setUpdateRequired(true);
  }, []);

  // Manual trigger for components that catch their own errors
  const triggerUpdateRequired = useCallback(() => {
    setUpdateRequired(true);
  }, []);

  useEffect(() => {
    // Listen for unhandled errors
    const handleError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error || event.message)) {
        event.preventDefault();
        handleChunkError();
      }
    };

    // Listen for unhandled promise rejections (async imports)
    const handleRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (error instanceof Error && isChunkLoadError(error)) {
        event.preventDefault();
        handleChunkError();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [isChunkLoadError, handleChunkError]);

  return {
    updateRequired,
    triggerUpdateRequired,
    isChunkLoadError,
  };
}

export default useChunkLoadingError;
